import React, { useState } from "react";
import { Button, Title } from "@yoast/ui-library";
import PropTypes from "prop-types";
import { Details } from "../../../Details";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import * as styles from "./OAuthGrantCardStyles.module.scss";
import { ExternalLink } from "../../../ExternalLink";
import { isValidHttpUrl, getRedirectHostnames, formatRelativeTime } from "./grantUtils";

const messages = defineMessages( {
	connectedOn: {
		id: "oauthGrant.connectedOn",
		defaultMessage: "Connected {date}",
	},
	lastUsed: {
		id: "oauthGrant.lastUsed",
		defaultMessage: "Last used {relativeTime}",
	},
	neverUsed: {
		id: "oauthGrant.neverUsed",
		defaultMessage: "Never used",
	},
	permissions: {
		id: "oauthGrant.permissions",
		defaultMessage: "This application can:",
	},
	revoke: {
		id: "oauthGrant.revoke",
		defaultMessage: "Disconnect",
	},
	revokeLabel: {
		id: "oauthGrant.revokeLabel",
		defaultMessage: "Disconnect {appName}",
	},
	providedBy: {
		id: "oauthGrant.providedBy",
		defaultMessage: "Provided by {link}",
	},
} );

/**
 * Card component displaying a single OAuth grant.
 *
 * Shows the client application name, logo, connection date, and granted permissions.
 * Uses a collapsible layout: compact summary always visible, permissions expand on click.
 *
 * @param {Object} props Component props.
 * @param {Object} props.grant The OAuth grant data.
 * @param {Function} props.onRevokeClick Callback when revoke button is clicked.
 * @param {boolean} [props.initialExpanded=false] Whether the card starts expanded.
 * @param {Object} props.intl The react-intl object.
 *
 * @returns {JSX.Element} The grant card component.
 */
function OAuthGrantCard( { grant, onRevokeClick, initialExpanded = false, intl } ) {
	const [ logoFailed, setLogoFailed ] = useState( false );
	const revokeLabel = intl.formatMessage( messages.revokeLabel, { appName: grant.clientName } );

	// Check if client is from yoast.com - if so, show redirect URIs instead of client URL
	let clientHostname = null;
	if ( isValidHttpUrl( grant.clientUri ) ) {
		clientHostname = new URL( grant.clientUri ).hostname;
	}
	const isYoastClient = clientHostname === "yoast.com";
	const redirectHostnames = getRedirectHostnames( grant.redirectUris );

	/**
	 * Gets the first letter of the client name for the placeholder logo.
	 *
	 * @param {string} name The client name.
	 *
	 * @returns {string} The first letter uppercase.
	 */
	const getClientInitial = ( name ) => {
		return name ? name.charAt( 0 ).toUpperCase() : "?";
	};

	/**
	 * Renders the client logo or a placeholder with the initial.
	 *
	 * @returns {React.ReactElement} The client logo element.
	 */
	const renderClientLogo = () => {
		if ( grant.clientLogoUri && ! logoFailed ) {
			return (
				<img
					src={ grant.clientLogoUri }
					alt=""
					className={ styles.logo }
					onError={ () => setLogoFailed( true ) }
				/>
			);
		}
		return (
			<div className={ styles.logoPlaceholder }>
				{ getClientInitial( grant.clientName ) }
			</div>
		);
	};

	/**
	 * Renders the "last used" text with relative time.
	 *
	 * @returns {React.ReactElement} The last used text.
	 */
	const renderLastUsed = () => {
		if ( ! grant.lastUsedAt ) {
			return <FormattedMessage { ...messages.neverUsed } />;
		}

		const relativeTime = formatRelativeTime( new Date( grant.lastUsedAt ), intl.locale );

		return (
			<FormattedMessage
				{ ...messages.lastUsed }
				values={ { relativeTime } }
			/>
		);
	};

	return (
		<Details initialOpenState={ initialExpanded } data-testid={ `oauth-grant-card-${ grant.grantId }` }>
			<Details.Summary>
				<div className={ styles.summaryContent }>
					{ renderClientLogo() }
					<div className={ styles.appInfo }>
						<div className={ styles.appDetails }>
							<Title as="h3" size="5">{ grant.clientName }</Title>
							{ isYoastClient && redirectHostnames.length > 0 && (
								<span className={ styles.hostnames }>
									{ redirectHostnames.join( ", " ) }
								</span>
							) }
							{ ! isYoastClient && isValidHttpUrl( grant.clientUri ) && (
								<ExternalLink
									href={ grant.clientUri }
									rel="noopener noreferrer"
									className={ styles.clientLink }
									onClick={ ( e ) => e.stopPropagation() }
								>
									{ new URL( grant.clientUri ).hostname }
								</ExternalLink>
							) }
						</div>
						<span className={ styles.lastUsed }>
							{ renderLastUsed() }
						</span>
					</div>
				</div>
			</Details.Summary>

			<Details.Content className={ styles.expandedContent }>
				<div className={ styles.permissions }>
					<p className={ styles.permissionsLabel }>
						<FormattedMessage { ...messages.permissions } />
					</p>
					<ul className={ styles.permissionsList }>
						{ grant.scopes.map( ( scope ) => (
							<li key={ scope.scope }>
								<ChevronRightIcon className={ styles.scopeIcon } aria-hidden="true" />
								<div className={ styles.scopeText }>
									<span className={ styles.scopeTitle }>{ scope.title }</span>
									{ scope.description && (
										<span className={ styles.scopeDescription }>{ scope.description }</span>
									) }
								</div>
							</li>
						) ) }
					</ul>
				</div>

				{ isYoastClient && isValidHttpUrl( grant.clientUri ) && (
					<p className={ styles.providedBy }>
						<FormattedMessage
							{ ...messages.providedBy }
							values={ {
								link: (
									<ExternalLink
										href={ grant.clientUri }
										rel="noopener noreferrer"
										className={ styles.clientLink }
									>
										{ new URL( grant.clientUri ).hostname }
									</ExternalLink>
								),
							} }
						/>
					</p>
				) }

				<div className={ styles.footer }>
					<Button
						variant="tertiary"
						size="small"
						className={ "yst-text-red-600" }
						onClick={ () => onRevokeClick( grant ) }
						aria-label={ revokeLabel }
					>
						<FormattedMessage { ...messages.revoke } />
					</Button>
				</div>
			</Details.Content>
		</Details>
	);
}

OAuthGrantCard.propTypes = {
	grant: PropTypes.shape( {
		grantId: PropTypes.string.isRequired,
		clientId: PropTypes.string.isRequired,
		clientName: PropTypes.string.isRequired,
		clientDescription: PropTypes.string,
		clientLogoUri: PropTypes.string,
		clientUri: PropTypes.string,
		redirectUris: PropTypes.arrayOf( PropTypes.string ),
		grantedAt: PropTypes.string.isRequired,
		lastUsedAt: PropTypes.string,
		scopes: PropTypes.arrayOf( PropTypes.shape( {
			scope: PropTypes.string.isRequired,
			title: PropTypes.string.isRequired,
			description: PropTypes.string.isRequired,
		} ) ).isRequired,
	} ).isRequired,
	onRevokeClick: PropTypes.func.isRequired,
	initialExpanded: PropTypes.bool,
	intl: PropTypes.object.isRequired,
};

export default injectIntl( OAuthGrantCard );
